import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  Boxes,
  ChevronRight,
  ClipboardList,
  Home,
  ListChecks,
  LogOut,
  Menu,
  PackageSearch,
  Settings,
  SlidersHorizontal,
  UserRound,
  Users,
  X
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/auth';
import { canManageWarehouse } from '@/utils/permissions';

interface NavigationItem {
  label: string;
  path: string;
  icon: typeof Home;
  adminOnly?: boolean;
  warehouseOnly?: boolean;
}

const primaryNavigation: NavigationItem[] = [
  { label: '首页', path: '/', icon: Home },
  { label: '样品列表', path: '/samples', icon: ListChecks },
  { label: '仓库管理', path: '/locations', icon: Boxes },
  { label: '入库管理', path: '/documents/create', icon: ArrowDownToLine },
  { label: '出库管理', path: '/documents/out_stock', icon: ArrowUpFromLine },
  { label: '调拨管理', path: '/documents/transfer', icon: ArrowRightLeft },
  { label: '借出管理', path: '/borrow', icon: PackageSearch },
  { label: '业务单据', path: '/documents', icon: ListChecks },
  { label: '报废管理', path: '/documents/scrap', icon: ClipboardList },
  { label: '销毁管理', path: '/documents/destroy', icon: Boxes }
];

const adminNavigation: NavigationItem[] = [
  { label: '库位管理', path: '/location-manage', icon: Settings, warehouseOnly: true },
  { label: '下拉选项', path: '/sample-dropdown-manage', icon: SlidersHorizontal, adminOnly: true },
  { label: '账号管理', path: '/user-manage', icon: Users, adminOnly: true }
];

const mobileNavigation: NavigationItem[] = [
  { label: '首页', path: '/', icon: Home },
  { label: '样品', path: '/samples', icon: ListChecks },
  { label: '入库', path: '/inventory-in', icon: ArrowDownToLine, warehouseOnly: true },
  { label: '出库', path: '/inventory-out', icon: ArrowUpFromLine, warehouseOnly: true },
  { label: '我的', path: '/account', icon: UserRound }
];

const routeTitles: Array<{ prefix: string; title: string }> = [
  { prefix: '/documents/create', title: '入库管理' },
  { prefix: '/documents/out_stock', title: '出库管理' },
  { prefix: '/documents/transfer', title: '调拨单据' },
  { prefix: '/documents/borrow', title: '借出单据' },
  { prefix: '/documents/return_sample', title: '归还单据' },
  { prefix: '/documents/scrap', title: '报废管理' },
  { prefix: '/documents/destroy', title: '销毁管理' },
  { prefix: '/documents', title: '业务单据' },
  { prefix: '/transfer-register', title: '调拨登记' },
  { prefix: '/scrap-register', title: '报废登记' },
  { prefix: '/destroy-register', title: '销毁登记' },
  { prefix: '/inventory-in', title: '入库登记' },
  { prefix: '/inventory-out', title: '出库登记' },
  { prefix: '/borrow-register', title: '申请借用' },
  { prefix: '/sample-dropdown-manage', title: '样衣下拉选项' },
  { prefix: '/location-manage', title: '库位管理' },
  { prefix: '/user-manage', title: '账号管理' },
  { prefix: '/locations', title: '仓库管理' },
  { prefix: '/samples/', title: '样品详情' },
  { prefix: '/samples', title: '样品列表' },
  { prefix: '/borrow', title: '借出管理' },
  { prefix: '/account', title: '我的账号' },
  { prefix: '/', title: '工作台' }
];

function isCurrentPath(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/') return currentPath === '/';
  return currentPath === itemPath || (itemPath !== '/documents' && currentPath.startsWith(`${itemPath}/`));
}

function DesktopNavigation({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const location = useLocation();
  const warehouse = canManageWarehouse(user);
  const navigation = [...primaryNavigation, ...adminNavigation].filter(item => (
    (!item.adminOnly || user?.role === 'admin') && (!item.warehouseOnly || warehouse)
  )).map(item => item.path === '/borrow' && !warehouse ? { ...item, label: '我的借用' } : item);

  return (
    <nav className='flex-1 overflow-y-auto px-3 py-4' aria-label='主导航'>
      <div className='space-y-1'>
        {navigation.map((item) => {
          const Icon = item.icon;
          const showAdminHeading = item.path === '/location-manage';
          return (
            <div key={item.path}>
              {showAdminHeading && (
                <p className='mb-2 mt-6 px-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400'>
                  管理功能
                </p>
              )}
              <NavLink
                to={item.path}
                end={item.path === '/' || item.path === '/documents'}
                onClick={onNavigate}
                className={({ isActive }) => `group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                  isActive || isCurrentPath(location.pathname, item.path)
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                }`}
              >
                <Icon aria-hidden='true' className='h-5 w-5 flex-none' />
                <span className='flex-1'>{item.label}</span>
                <ChevronRight aria-hidden='true' className='h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100' />
              </NavLink>
            </div>
          );
        })}
      </div>
    </nav>
  );
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const warehouse = canManageWarehouse(user);
  const mobileItems = warehouse ? mobileNavigation : [
    mobileNavigation[0], mobileNavigation[1],
    { label: '我的借用', path: '/borrow', icon: PackageSearch },
    mobileNavigation[4]
  ];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pageTitle = location.pathname === '/borrow' && !warehouse ? '我的借用' : routeTitles.find((route) => (
    route.prefix === '/' ? location.pathname === '/' : location.pathname.startsWith(route.prefix)
  ))?.title || '样品管理系统';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className='min-h-screen bg-slate-50 text-slate-900'>
      <aside className='fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-slate-200 bg-white lg:flex'>
        <div className='flex h-20 items-center border-b border-slate-100 px-6'>
          <img src='/korrun-logo.png' alt='Korrun' className='h-12 w-28 object-contain object-left' />
          <div className='ml-3 min-w-0 border-l border-slate-200 pl-3'>
            <p className='truncate text-sm font-bold text-slate-900'>样品管理</p>
            <p className='text-xs text-slate-500'>SampleFlow</p>
          </div>
        </div>
        <DesktopNavigation />
        <div className='border-t border-slate-100 p-4'>
          <NavLink
            to='/account'
            className='flex min-h-12 items-center gap-3 rounded-xl px-3 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600'
          >
            <span className='flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 font-bold text-emerald-800'>
              {(user?.name || '用').charAt(0)}
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block truncate text-sm font-semibold text-slate-900'>{user?.name || '当前用户'}</span>
              <span className='block truncate text-xs text-slate-500'>{user?.department || '未分配事业部'}</span>
            </span>
          </NavLink>
        </div>
      </aside>

      <div className='min-h-screen lg:pl-72'>
        <header className='sticky top-0 z-30 hidden h-20 items-center justify-between border-b border-slate-200 bg-white/95 px-8 backdrop-blur lg:flex'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700'>SampleFlow</p>
            <h1 className='mt-1 text-xl font-bold text-slate-950'>{pageTitle}</h1>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-sm text-slate-600'>{user?.name}</span>
            <button
              type='button'
              onClick={handleLogout}
              className='flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600'
            >
              <LogOut aria-hidden='true' className='h-4 w-4' />
              退出
            </button>
          </div>
        </header>

        <header className='sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:hidden'>
          <div className='flex min-w-0 items-center gap-3'>
            <img src='/korrun-logo.png' alt='Korrun' className='h-9 w-20 object-contain object-left' />
            <h1 className='truncate border-l border-slate-200 pl-3 text-base font-bold text-slate-900'>{pageTitle}</h1>
          </div>
          <button
            type='button'
            onClick={() => setMobileMenuOpen(true)}
            className='flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600'
            aria-label='打开全部导航'
          >
            <Menu aria-hidden='true' className='h-6 w-6' />
          </button>
        </header>

        <main className='pb-24 lg:pb-0'>
          <div className='mx-auto w-full max-w-[1440px] p-4 sm:p-6 lg:p-8'>
            <Outlet />
          </div>
        </main>
      </div>

      <nav className={`fixed inset-x-0 bottom-0 z-30 grid h-20 ${warehouse ? 'grid-cols-5' : 'grid-cols-4'} border-t border-slate-200 bg-white px-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(15,23,42,0.08)] lg:hidden`} aria-label='移动导航'>
        {mobileItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-600 ${
                isActive || isCurrentPath(location.pathname, item.path) ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              <Icon aria-hidden='true' className='h-5 w-5' />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {mobileMenuOpen && (
        <div className='fixed inset-0 z-50 flex lg:hidden'>
          <button
            type='button'
            className='absolute inset-0 cursor-default bg-slate-950/45'
            onClick={() => setMobileMenuOpen(false)}
            aria-label='关闭导航'
          />
          <aside className='relative ml-auto flex h-full w-[min(88vw,360px)] flex-col bg-white shadow-2xl'>
            <div className='flex h-16 items-center justify-between border-b border-slate-100 px-4'>
              <div className='flex items-center gap-3'>
                <img src='/korrun-logo.png' alt='Korrun' className='h-10 w-24 object-contain object-left' />
                <span className='font-bold text-slate-900'>全部功能</span>
              </div>
              <button
                type='button'
                onClick={() => setMobileMenuOpen(false)}
                className='flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600'
                aria-label='关闭导航'
              >
                <X aria-hidden='true' className='h-5 w-5' />
              </button>
            </div>
            <DesktopNavigation onNavigate={() => setMobileMenuOpen(false)} />
          </aside>
        </div>
      )}
    </div>
  );
}
